import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, ExternalLink, Loader2, Send, XCircle } from "lucide-react";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export async function openSentFile(projectId, logId, idx) {
  // Fenster sofort öffnen (iOS/Safari-Popup-Blocker), Inhalt danach laden.
  const win = window.open("", "_blank");
  try {
    const r = await api.get(`/projects/${projectId}/sent-documents/${logId}/${idx}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    if (win) {
      win.location.href = url;
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    if (win) win.close();
    toast.error("Dokument konnte nicht geöffnet werden");
  }
}

function SentRow({ item, projectId }) {
  return (
    <li className="py-3 first:pt-0 last:pb-0" data-testid={`sent-doc-${item.id}`}>
      <div className="flex items-start gap-2">
        {item.ok
          ? <CheckCircle2 size={15} className="text-[#00E575] mt-0.5 shrink-0" />
          : <XCircle size={15} className="text-red-400 mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-zinc-100">{item.label}</div>
          <div className="text-[12px] text-zinc-300 break-words">
            an <b data-testid={`sent-doc-recipient-${item.id}`}>{item.recipient_email || "—"}</b>
            {item.cc?.length ? <> · CC {item.cc.join(", ")}</> : null}
          </div>
          <div className="text-[11px] text-zinc-500">
            {fmt(item.sent_at)}{item.sent_by_name ? ` · von ${item.sent_by_name}` : ""}
            {!item.ok && <span className="text-red-300"> · NICHT versendet: {item.error || "Fehler"}</span>}
          </div>
          {item.note && <div className="mt-1 text-[11.5px] text-zinc-400 whitespace-pre-line border-l-2 border-[#00E575]/50 pl-2">{item.note}</div>}
          {item.team_bcc?.length > 0 && (
            <div className="text-[11px] text-zinc-500 break-words" data-testid={`sent-doc-team-${item.id}`}>Kopie (BCC) an Team: {item.team_bcc.join(", ")}</div>
          )}
          {item.files.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {item.files.map((f) => (
                <button key={f.idx} type="button" disabled={!f.available}
                  onClick={() => openSentFile(projectId, item.id, f.idx)}
                  data-testid={`sent-doc-open-${item.id}-${f.idx}`}
                  className="h-8 max-w-full px-2.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-[11.5px] text-zinc-100 inline-flex items-center gap-1 disabled:opacity-40">
                  <ExternalLink size={11} className="shrink-0" /> <span className="truncate">{f.filename}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// Alle per E-Mail versendeten Dokumente eines Projekts (Status + Dokumente).
export function SentDocumentsPanel({ projectId, collapsible = false, reloadKey }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(!collapsible);
  const load = useCallback(() => {
    api.get(`/projects/${projectId}/sent-documents`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [projectId]);
  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid="sent-documents-panel">
      <button type="button" onClick={() => collapsible && setOpen((o) => !o)} data-testid="sent-documents-toggle"
        className="w-full flex items-center justify-between gap-2 text-left">
        <span className="inline-flex items-center gap-2 font-display font-semibold text-zinc-100 text-[14px]">
          <Send size={15} className="text-[#00E575]" /> Versendete Dokumente
          {items && <span className="text-[11px] text-zinc-400 font-normal">({items.length})</span>}
        </span>
        {collapsible && <ChevronDown size={16} className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && (
        <div className="mt-3">
          {items === null ? (
            <div className="text-sm text-zinc-400 inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Lade …</div>
          ) : items.length === 0 ? (
            <p className="text-[12.5px] text-zinc-400">Zu diesem Projekt wurde noch nichts per E-Mail versendet.</p>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {items.map((it) => <SentRow key={it.id} item={it} projectId={projectId} />)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
